import {
  format,
  startOfToday,
  endOfToday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  addDays,
  subDays,
} from "date-fns";

// ─── ALL AVAILABLE FILTER OPTIONS ─────────────────────────────────────────────
// Pick and choose which ones you want in each page
export const ALL_RANGE_OPTIONS = [
  { key: "today",         label: "Today" },
  { key: "yesterday",     label: "Yesterday" },
  { key: "tomorrow",      label: "Tomorrow" },
  { key: "this_week",     label: "This Week" },
  { key: "this_month",    label: "This Month" },
  { key: "last_month",    label: "Last Month" },
  { key: "last_3_months", label: "Last 3 Months" },
  { key: "last_6_months", label: "Last 6 Months" },
  { key: "last_1_year",   label: "Last 1 Year" },
  { key: "all",           label: "All" },
];

// ─── COMPUTE DATE RANGE FOR A GIVEN KEY ───────────────────────────────────────
// Returns { start: "yyyy-MM-dd HH:mm:ss", end: "yyyy-MM-dd HH:mm:ss" }
// or { start: null, end: null } for "all" / unknown
export const getDateRangeForKey = (key) => {
  const now = new Date();
  const fmt = (d) => format(d, "yyyy-MM-dd HH:mm:ss");

  switch (key) {
    case "today":
      return {
        start: fmt(startOfToday()),
        end:   fmt(endOfToday()),
      };

    case "yesterday": {
      const yesterday = subDays(now, 1);
      return {
        start: fmt(new Date(yesterday.setHours(0, 0, 0, 0))),
        end:   fmt(new Date(new Date(subDays(now, 1)).setHours(23, 59, 59, 999))),
      };
    }

    case "tomorrow": {
      const tomorrow = addDays(now, 1);
      return {
        start: fmt(new Date(tomorrow.setHours(0, 0, 0, 0))),
        end:   fmt(new Date(new Date(addDays(now, 1)).setHours(23, 59, 59, 999))),
      };
    }

    case "this_week":
      return {
        start: fmt(startOfWeek(now, { weekStartsOn: 1 })), // Monday
        end:   fmt(endOfWeek(now, { weekStartsOn: 1 })),   // Sunday
      };

    case "this_month":
      return {
        start: fmt(startOfMonth(now)),
        end:   fmt(endOfMonth(now)),
      };

    case "last_month": {
      const lm = subMonths(now, 1);
      return {
        start: fmt(startOfMonth(lm)),
        end:   fmt(endOfMonth(lm)),
      };
    }

    case "last_3_months":
      return {
        start: fmt(startOfMonth(subMonths(now, 3))),
        end:   fmt(endOfMonth(subMonths(now, 1))), // end of PREVIOUS month
      };

    case "last_6_months":
      return {
        start: fmt(startOfMonth(subMonths(now, 6))),
        end:   fmt(endOfMonth(subMonths(now, 1))), // end of PREVIOUS month
      };

    case "last_1_year":
      return {
        start: fmt(startOfMonth(subMonths(now, 12))),
        end:   fmt(endOfMonth(subMonths(now, 1))), // end of PREVIOUS month
      };

    case "all":
    default:
      return { start: null, end: null };
  }
};

// ─── DISPLAY FORMAT (for date inputs yyyy-MM-dd) ───────────────────────────────
export const getDisplayRangeForKey = (key) => {
  const range = getDateRangeForKey(key);
  return {
    start: range.start ? range.start.split(" ")[0] : "",
    end:   range.end   ? range.end.split(" ")[0]   : "",
  };
};

// ─── MATCH CUSTOM DATES BACK TO A KEY ─────────────────────────────────────────
// So when user manually picks dates that match a predefined range,
// the dropdown auto-selects that range
export const matchDatesToRangeKey = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  for (const option of ALL_RANGE_OPTIONS) {
    if (option.key === "all") continue;
    const range = getDisplayRangeForKey(option.key);
    if (range.start === startDate && range.end === endDate) {
      return option.key;
    }
  }
  return null; // custom
};